import React, { useEffect, useState, useRef } from 'react';
import { api } from '../lib/api';
import { useAuth } from '../lib/auth';
import { AlarmClock, X } from 'lucide-react';

/**
 * Clinician appointment alarm. Polls /upcoming-alarms every 60s, fires a single
 * toast + Web Notification when an appointment is within the configured window.
 */
export default function AppointmentAlarm() {
  const { user } = useAuth();
  const [alarms, setAlarms] = useState([]);
  const [minutes, setMinutes] = useState(10);
  const [dismissed, setDismissed] = useState(new Set());
  const [paneOpen, setPaneOpen] = useState(false);
  const firedRef = useRef(new Set());

  const load = async () => {
    if (user?.role !== 'clinician' && user?.role !== 'admin') return;
    try {
      const { data } = await api.get(`/clinicians/${user.user_id}/upcoming-alarms`);
      setAlarms(data.alarms || []);
      setMinutes(data.alarm_minutes || 0);
    } catch (_) { /* feature may be off */ }
  };
  useEffect(() => {
    load();
    const t = setInterval(load, 60000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.user_id]);

  useEffect(() => {
    // Fire browser notification once per appointment_id when it first enters the window.
    for (const a of alarms) {
      if (firedRef.current.has(a.appointment_id) || dismissed.has(a.appointment_id)) continue;
      firedRef.current.add(a.appointment_id);
      try {
        if ('Notification' in window && Notification.permission === 'granted') {
          new Notification('Upcoming appointment', {
            body: `${a.starts_at?.slice(11, 16)} · ${a.therapist_name || 'session'}`,
          });
        }
      } catch (_) { /* noop */ }
    }
  }, [alarms, dismissed]);

  if (!minutes || (user?.role !== 'clinician' && user?.role !== 'admin')) return null;
  const visible = alarms.filter(a => !dismissed.has(a.appointment_id));
  if (!visible.length) return null;

  const askPermission = async () => {
    try { if ('Notification' in window && Notification.permission === 'default') await Notification.requestPermission(); } catch (_) {}
  };

  return (
    <div className="fixed bottom-4 right-4 z-50 print:hidden" data-testid="alarm-widget">
      {!paneOpen ? (
        <button onClick={()=>{ setPaneOpen(true); askPermission(); }}
                className="surface border-2 border-warn shadow-lg rounded-full px-4 py-2 flex items-center gap-2 animate-pulse"
                data-testid="alarm-pill">
          <AlarmClock className="w-4 h-4 text-warn"/>
          <span className="text-sm font-medium">{visible.length} upcoming · {minutes}min</span>
        </button>
      ) : (
        <div className="surface border border-soft shadow-xl rounded-xl p-4 w-80">
          <div className="flex items-center justify-between mb-2">
            <h4 className="font-heading font-semibold text-sm flex items-center gap-2">
              <AlarmClock className="w-4 h-4 text-warn"/> Upcoming · {minutes} min window
            </h4>
            <button onClick={()=>setPaneOpen(false)} className="text-fg-muted hover:text-fg"><X className="w-4 h-4"/></button>
          </div>
          <div className="space-y-2 max-h-[300px] overflow-y-auto">
            {visible.map(a => (
              <div key={a.appointment_id} className="flex items-center gap-2 text-sm border-b border-soft pb-2" data-testid={`alarm-row-${a.appointment_id}`}>
                <div className="flex-1">
                  <div className="font-mono text-xs text-brand">{a.starts_at?.slice(11, 16)}</div>
                  <div className="text-xs text-fg-muted truncate">{a.therapist_name || 'Session'} · {a.duration_min}m</div>
                </div>
                <button onClick={()=>setDismissed(d => new Set(d).add(a.appointment_id))}
                        className="text-fg-muted hover:text-fg text-xs" data-testid={`alarm-dismiss-${a.appointment_id}`}>
                  Dismiss
                </button>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
